import ContextMenu from '@atlas/uicorn/lib/Control/ContextMenu';
import IconButton from '@atlas/uicorn/lib/Control/IconButton';
import OptionsList, {
    SOptionsListItem,
    type TOptionsListChooseHandler,
    type TOptionsListItem,
    type TOptionsListProps,
    type TOptionsListSortHandler,
    optionListItemEffectStyles,
    SOptionsListButton,
    SOptionsListItemText,
    optionListItemActiveStyles,
    SOptionListItemDescription,
} from '@atlas/uicorn/lib/Control/OptionsList';
import ShowMoreIcon from '@atlas/uicorn/lib/Icon/ShowMoreIcon';
import { registerStylesheet } from '@atlas/uicorn/lib/System/StylesProvider';
import type { TExample } from '@atlas/uicorn/preview/modules';
import { useMemo } from 'react';
import * as React from 'react';
import styled, { css, createGlobalStyle } from 'styled-components';
import useRefCallback from 'use-ref-callback';

const TabsNew = React.memo(function TabsNew(props: TTabsNewProps): React.ReactElement {
    const {
        items: propsItems,
        preferIcon,
        createLabel,
        createIcon,
        onChoose,
        onCreate,
        horizontal = true,
        ...rest
    } = props;

    const handleCreateClick = useRefCallback<React.MouseEventHandler<HTMLButtonElement>>((event) => {
        event.stopPropagation();
        onCreate?.(null);
    });

    const items = useMemo(() => {
        let result = propsItems;
        const defaultClassName = horizontal ? tabOptionClassName : verticalTabOptionClassName;

        result = result.map(({ actions, ...item }) => ({
            ...item,
            suffix: actions?.length ? <ContextMenu items={actions} children={<STabsNewOptionsButton />} /> : undefined,
            className: item.className ? [item.className, defaultClassName].join(' ') : defaultClassName,
        }));

        if (preferIcon) {
            result = result.map((item) =>
                item.icon ? { ...item, label: '', tooltip: item.tooltip ?? item.label } : item,
            );
        }

        if (onCreate) {
            result = result.concat({
                key: addOptionKey,
                className: 'tabs-add-option',
                label: createLabel || '+ Add',
                icon: createIcon,
                onClick: handleCreateClick,
            });
        }

        return result;
    }, [propsItems, preferIcon, createLabel, onCreate, horizontal]);

    const handleChoose = useRefCallback<TOptionsListChooseHandler>((option, selection) => {
        const chosenOption = propsItems.find((item) => item.key === option.key);
        if (chosenOption && onChoose) onChoose(chosenOption, selection);
    });

    return <STabsNew {...rest} inlineDescription horizontal={horizontal} items={items} onChoose={handleChoose} />;
});

export default TabsNew;

// #region Helpers

const tabOptionClassName = 'atlas-tabs-new-tab';
const verticalTabOptionClassName = `${tabOptionClassName}-vertical`;

const addOptionKey = 'create';

// #endregion

// #region Styles

export const STabsNewOptionsButton = styled(IconButton).attrs({
    elementAs: 'span',
    icon: <ShowMoreIcon />,
    tooltip: '',
    tabIndex: -1,
})``;

const STabsNew = styled(OptionsList)`
    ${(p) => p.theme.patch.noScroll()};
    flex-direction: ${(p) => (p.horizontal ? 'row' : 'column')};

    & {
        ${(p) =>
            !p.horizontal &&
            css`
                border-radius: 0;
            `}
    }

    ${SOptionsListButton} {
        ${(p) =>
            !p.horizontal &&
            css`
                border-radius: 0;
                border-right-width: 0.2rem;
                border-right-style: solid;
                border-right-color: ${p.theme.color.grey0};
            `};

        ${(p) =>
            !p.horizontal &&
            optionListItemActiveStyles(css`
                border-right-color: ${p.theme.color.primary};
            `)};
    }

    ${SOptionsListItemText} {
        overflow: unset;

        ${(p) =>
            !p.horizontal &&
            css`
                width: 100%;
                min-width: 0;
                justify-content: space-between;
            `}
    }

    ${SOptionsListItem} {
        ${(p) =>
            !p.horizontal &&
            css`
                &:has(> ${SOptionsListButton}[aria-label=${addOptionKey}])::after {
                    content: '';
                    height: 0.1rem;
                    display: block;
                    width: calc(100% - 1.6rem);
                    background-color: ${(p) => p.theme.color.grey6};
                    margin: 2.4rem 0 1.6rem 0;
                }
            `}
    }

    .tabs-add-option {
        color: ${(p) => (p.horizontal ? p.theme.color.grey8 : p.theme.color.grey7)};

        ${(p) =>
            !p.horizontal &&
            css`
                &:hover {
                    color: ${(p) => p.theme.color.grey8};
                    background-color: transparent;
                }
            `}
    }
`;

// This have to be outside applied outside of Tabs because option is mounted into the body when dragged
const TabStylesheet = createGlobalStyle`
    .${tabOptionClassName} {
        padding: 0.8rem 0.4rem;

        position: relative;
        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 50%;
            right: 50%;
            background: ${(p) => p.theme.color.primary};
            opacity: 0;
            height: 0;

            transition: left 0.2s ease, right 0.2s ease, opacity 0.2s ease, height 0.2s ease;
        }

        ${(p) =>
            optionListItemEffectStyles(css`
                background: transparent;
                color: ${p.theme.color.primary};

                &::after {
                    left: 0%;
                    right: 0%;
                    opacity: 1;
                    height: 0.2rem;
                }
            `)}
    }

    .${verticalTabOptionClassName} {
        --vertical-margin: 1rem;
        padding: var(--vertical-margin) 1.6rem var(--vertical-margin) 0.4rem;
        color: ${(p) => p.theme.color.grey8};

        ${(p) =>
            optionListItemEffectStyles(css`
                background: transparent;
                color: ${p.theme.color.primary};
            `)};

        ${(p) =>
            optionListItemActiveStyles(css`
                ${SOptionListItemDescription} {
                    color: ${p.theme.color.grey9};
                }
            `)};
    }

    body > ${SOptionsListItem} {
        > .${tabOptionClassName}, > .${verticalTabOptionClassName} {
            background: ${(p) => p.theme.color.grey0};
            border-radius: ${(p) => p.theme.radius.small};
            box-shadow: ${(p) => p.theme.shadow.low};

            &::after {
                display: none;
            }
        }
    }
`;

registerStylesheet(TabStylesheet);

// #endregion

// #region Types

export type TTabsItem = Omit<TOptionsListItem, 'group' | 'selectable' | 'selected' | 'prefix' | 'suffix'> & {
    actions?: TOptionsListItem[];
};

export type TTabsSortHandler = TOptionsListSortHandler;

export type TTabsNewProps = Omit<TOptionsListProps, 'items' | 'selected' | 'defaultSelected'> & {
    items: TTabsItem[];
    preferIcon?: boolean;
    createLabel?: string;
    createIcon?: React.ReactNode;
    onCreate?: (text: string | null) => void;
    onSort?: TTabsSortHandler;
};

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        const items = React.useMemo(
            () =>
                new Array(props.items ?? 0)
                    .fill(null)
                    .map<TTabsItem>((_, i) => ({
                        key: `tab-${i + 1}`,
                        label: `Tab ${i + 1}`,
                        ...(props.withDescription && {
                            description: i === 0 ? undefined : Math.round(Math.random() * 9),
                        }),
                        ...(props.withTooltip && { tooltip: `Tooltip for "Tab ${i + 1}"` }),
                        ...(props.withDanger && i === 0 && { danger: true }),
                        ...(props.disabled === 'First one' && i === 0 && { disabled: true }),
                    }))
                    .concat(
                        props.icons?.startsWith('Some has')
                            ? [
                                  {
                                      key: 'with-icon',
                                      label: 'Tab with icon',
                                      icon: '🍫',
                                      ...(props.withTooltip && { tooltip: 'Chocolate' }),
                                  },
                              ]
                            : [],
                    )
                    .map((item, index, { length }) =>
                        props.withEnding && (!index || index === length - 1) ? { ...item, end: true } : item,
                    ),
            [
                props.items,
                props.disabled,
                props.icons,
                props.withDescription,
                props.withTooltip,
                props.withDanger,
                props.withEnding,
            ],
        );

        return (
            <TabsNew
                items={items}
                disabled={props.disabled === 'All of them'}
                preferIcon={props.icons === 'Some has and preferIcon is ON'}
                createLabel={props.createOption}
                horizontal={props.horizontal}
                onChoose={props.onChoose}
                onCreate={typeof props.createOption === 'string' ? props.onCreate : undefined}
                style={{ ...(props.withEnding && { minWidth: '30.0rem' }) }}
            />
        );
    },
    controls: {
        items: { type: 'number', initial: 3, min: 1 },
        disabled: ['Not at all', 'All of them', 'First one'],
        icons: ['Not set', 'Some has', 'Some has and preferIcon is ON'],
        createOption: { type: 'string', initial: '+ Create new', connected: false },
        withDescription: 'boolean',
        withTooltip: 'boolean',
        withDanger: 'boolean',
        withEnding: 'boolean',
        horizontal: { type: 'boolean', initial: true },
    },
    handlers: {
        onChoose: 'console',
        onCreate: 'console',
    },
    figma: 'https://www.figma.com/file/YK0CuM3dK7LcMbVtTUuL8I/Product----Web-2.0?type=design&node-id=2698-32083&mode=design&t=Uwcd783en5y1PF6w-11',
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('TabsNew', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
